import React, { useEffect, useState } from "react";
import { BellRing, FileDown, Send } from "lucide-react";
import { toast } from "sonner";
import { araciBildirim, crud } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function AraciBildirimGonderPage() {
    const [templates, setTemplates] = useState([]);
    const [aracilar, setAracilar] = useState([]);
    const [templateKey, setTemplateKey] = useState("yeni_dosya");
    const [selected, setSelected] = useState([]);
    const [baslik, setBaslik] = useState("");
    const [icerik, setIcerik] = useState("");
    const [dosyaId, setDosyaId] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        araciBildirim.templates().then((d) => setTemplates(d.templates || [])).catch(() => {});
        crud.list("aracilar", { aktif: true, page_size: 200 })
            .then((d) => setAracilar(d.results || d || []))
            .catch(() => toast.error("Aracılar yüklenemedi."));
    }, []);

    useEffect(() => {
        const tpl = templates.find((t) => t.key === templateKey);
        if (tpl && templateKey !== "genel") {
            setBaslik(tpl.baslik || "");
        }
    }, [templateKey, templates]);

    function toggleAraci(id) {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    }

    async function handleSend() {
        if (selected.length === 0) {
            toast.error("En az bir aracı seçin.");
            return;
        }
        setSending(true);
        try {
            const payload = {
                template_key: templateKey,
                araci_ids: selected,
            };
            if (dosyaId) payload.sigorta_dosyasi = Number(dosyaId);
            if (templateKey === "genel") {
                payload.baslik = baslik;
                payload.icerik = icerik;
            }
            const res = await araciBildirim.send(payload);
            toast.success(`${res.gonderilen} aracıya bildirim gönderildi.`);
            setSelected([]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Gönderim başarısız.");
        } finally {
            setSending(false);
        }
    }

    async function handlePdf() {
        try {
            const r = await araciBildirim.ozetPdf(selected[0]);
            const blob = new Blob([r.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            toast.error("PDF oluşturulamadı.");
        }
    }

    return (
        <div className="space-y-6 max-w-4xl">
            <header>
                <div className="flex items-center gap-3 mb-1">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[rgba(212,175,55,0.15)] text-[#D4AF37]">
                        <BellRing className="h-5 w-5" />
                    </span>
                    <div>
                        <h1 className="font-display text-2xl gold-text">Aracı bildirim gönder</h1>
                        <p className="text-sm text-[var(--mbs-ink-3)]">
                            Şablon seçin, aracıları işaretleyin ve tek tıkla gönderin
                        </p>
                    </div>
                </div>
            </header>

            <section className="rounded-xl border border-[var(--mbs-border)] bg-[var(--mbs-surface)] p-5 space-y-4">
                <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-[var(--mbs-ink-2)]">
                        Şablon
                    </label>
                    <select
                        value={templateKey}
                        onChange={(e) => setTemplateKey(e.target.value)}
                        className="mt-2 w-full rounded-md bg-[#0a1220] border border-[var(--mbs-border)] px-3 py-2 text-sm"
                    >
                        {templates.map((t) => (
                            <option key={t.key} value={t.key}>
                                {t.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-[var(--mbs-ink-2)]">
                        Sigorta dosyası ID (opsiyonel)
                    </label>
                    <input
                        type="number"
                        value={dosyaId}
                        onChange={(e) => setDosyaId(e.target.value)}
                        placeholder="Dosya ID — şablonda {dosya_no} dolar"
                        className="mt-2 w-full rounded-md bg-[#0a1220] border border-[var(--mbs-border)] px-3 py-2 text-sm"
                    />
                </div>

                {templateKey === "genel" && (
                    <>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-widest text-[var(--mbs-ink-2)]">
                                Başlık
                            </label>
                            <input
                                value={baslik}
                                onChange={(e) => setBaslik(e.target.value)}
                                className="mt-2 w-full rounded-md bg-[#0a1220] border border-[var(--mbs-border)] px-3 py-2 text-sm"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-widest text-[var(--mbs-ink-2)]">
                                İçerik (HTML)
                            </label>
                            <Textarea
                                value={icerik}
                                onChange={(e) => setIcerik(e.target.value)}
                                rows={5}
                                className="mt-2 bg-[#0a1220] border-[var(--mbs-border)]"
                            />
                        </div>
                    </>
                )}

                <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-[var(--mbs-ink-2)]">
                        Aracılar ({selected.length} seçili)
                    </label>
                    <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-[var(--mbs-border)] divide-y divide-[var(--mbs-border)]">
                        {aracilar.map((a) => (
                            <label
                                key={a.id}
                                className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5 text-sm"
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.includes(a.id)}
                                    onChange={() => toggleAraci(a.id)}
                                />
                                <span>{a.ad}</span>
                                <span className="text-[var(--mbs-ink-3)] text-xs ml-auto">{a.email}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                        onClick={handleSend}
                        disabled={sending || selected.length === 0}
                        className="bg-[#D4AF37] text-slate-900 hover:bg-[#c9a430]"
                    >
                        <Send className="h-4 w-4 mr-2" />
                        Gönder
                    </Button>
                    <Button variant="outline" onClick={handlePdf}>
                        <FileDown className="h-4 w-4 mr-2" />
                        Bildirim özeti PDF
                    </Button>
                </div>
            </section>
        </div>
    );
}
